import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { debounce } from 'lodash'
import * as S from '../styles'
import { formatNumberBR } from 'src/utils'
import { SalesReceivementBatch } from 'src/features/receivement/ReceivementTypes'
import { dropdownOptions } from './ActionDropdown'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from 'src/features/receivement/ReceivementState'

interface Props {
  batch: SalesReceivementBatch;
  salesPerDay: number;
  quantityErrorMessage?: string;
  handleErrorQuantity: (value: number, option?: string) => void;
}

export const AcceptQuantityInput = React.memo(
  ({
    batch,
    salesPerDay,
    quantityErrorMessage,
    handleErrorQuantity,
  }: Props) => {
    const dispatch = useDispatch()
    const batchState = useSelector(
      (state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')],
    )

    const formatInputNumber = useCallback(
      (acceptedQuantity) => {
        if (typeof acceptedQuantity !== 'number') {
          return ''
        }

        if (batch.measureUnit.isKg) {
          return formatNumberBR(acceptedQuantity, 3)
        }

        return formatNumberBR(acceptedQuantity)
      },
      [batch.measureUnit.isKg],
    )

    const acceptedQuantityFormatted = useMemo(
      () => formatInputNumber(batchState.acceptedQuantity),
      [batchState.acceptedQuantity, formatInputNumber],
    )

    const [localValue, setLocalValue] = useState(acceptedQuantityFormatted)

    const prevFormattedRef = useRef(acceptedQuantityFormatted)
    useEffect(() => {
      if (prevFormattedRef.current !== acceptedQuantityFormatted) {
        prevFormattedRef.current = acceptedQuantityFormatted
        setLocalValue(acceptedQuantityFormatted)
      }
    }, [acceptedQuantityFormatted])

    const debouncedDispatch = useMemo(
      () => debounce((payload) => dispatch(actions.updateBatchState(payload)), 0),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    )

    useEffect(() => () => debouncedDispatch.cancel(), [debouncedDispatch])

    const handleQuantity = useCallback((event) => {
      const rawValue = event.target.value
      setLocalValue(rawValue)

      let value = parseInt(rawValue.replace(/[^0-9]/g, ''), 10)
      if (batch.measureUnit.isKg) {
        value /= 1000
      }

      handleErrorQuantity(
        value,
        batchState.option ? dropdownOptions[batchState.option] : undefined,
      )

      if (Number.isNaN(value)) {
        debouncedDispatch({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: 0,
            option: batchState.option,
          },
        })
        return
      }

      const acceptDamage = value > batch.usefulQuantity
      const rawReturn = batch.totalQuantity - value
      const returnQuantity = batch.measureUnit.isKg
        ? Math.round(rawReturn * 1000) / 1000
        : Math.round(rawReturn)

      debouncedDispatch({
        [batch.idsBatchItemOrder.join(',')]: {
          ...batchState,
          acceptedQuantity: value,
          returnQuantity,
          stockDays: salesPerDay !== 0 ? value / salesPerDay : 0,
          acceptDamage,
          option: batchState.option,
        },
      })
    }, [batch, batchState, salesPerDay, handleErrorQuantity, debouncedDispatch])

    return (
      <S.QuantityWrapper>
        <S.Input
          aria-label='Quantidade a ser aceita do lote'
          data-testid='qty-input'
          type='text'
          error={!!quantityErrorMessage}
          value={localValue}
          onChange={handleQuantity}
          onBlur={() => debouncedDispatch.flush()}
          readOnly={
            batchState.option
              ? dropdownOptions[batchState.option] !== dropdownOptions.PARTIAL
              : true
          }
        />
        {quantityErrorMessage && <S.Error>{quantityErrorMessage}</S.Error>}
      </S.QuantityWrapper>
    )
  },
)
